import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError, map, Observable, of, delay, switchMap } from 'rxjs';
import { STAT_DATA } from '../store/dashboard/dashboard.proxy';
import { ConfigurationItemDescriptor, ConfigurationItemValue, PagedResponse } from 'src/app/pages/configuration/configuration.model';
import { IAddonPayload } from './add-on.service';
import { WebhookSubscription, WebhookTestRequest } from 'src/app/pages/dashboard/settings/webhooks/webhook-subscription.model';
import {
  AutoEmailEngagementActivateRequest,
  AutoEmailEngagementActivateResponse,
  AutoEmailEngagementInitResponse,
  AutoEmailTemplateResponse,
} from 'src/app/pages/dashboard/vacancies/vacancies-match/components/auto-email-engagement/auto-email-engagement.models';
import {
  AutoEmailEngagementSequencesQuery,
  AutoEmailEngagementSequencesResponse,
  SEQUENCE_CHUNK_SIZE,
} from 'src/app/pages/dashboard/vacancies/vacancies-match/components/sequences-workspace/sequences-workspace.models';
import {
  CandidateEnrichmentBatchDetailDto,
  CandidateEnrichmentBatchListDto,
  CandidateEnrichmentBatchQuery,
  CandidateEnrichmentRowsQuery,
} from 'src/app/pages/upload-cvs/upload-cvs.models';
import { RecruiterActivityRequestModel } from './recruiter-activity-analytics.service.type';
import { CandidateEmailEngagementHistory } from 'src/app/core/models/candidate-email-engagement.model';
import {
  DEEPMATCH_CONFIG_GROUP,
  DEEPMATCH_CONFIG_NAME,
  DeepMatchEffectiveSettings,
  DeepMatchPriorityWeights
} from 'src/app/core/models/deepmatch-priority.model';
import { ViewedContact } from './candidate-contact.service';

export enum USER_ROLES {
	talent = 'talent',
  partner = 'partner',
	hm = 'hm',
	admin = 'admin',
	accountAdmin = 'account-admin',
}
	
export enum RequirementLevel{
    LowPriority,
    NiceToHave,
    CouldHave,
    ShouldHave,
    MustHave
  }

@Injectable({ providedIn: 'root' })
export class ApiService {

  baseDomain = environment.baseDomain + "/api/v1";

  constructor(private httpClient: HttpClient) {
  }

  //////////// User

  getUser() {
    return this.httpClient.get(`${this.baseDomain}/user`, { withCredentials: true }).pipe(map((res: any) => res));
  }

  createUser(email: string,role:USER_ROLES) {
  return this.httpClient.post(`${this.baseDomain}/user/${email}/${role}`, {});
  }

  deleteUser() {
    return this.httpClient.delete(`${this.baseDomain}/user`);
  }

  createApiKey(payload: { name: string; username: string; scopes: string[]; expireAt?: string }) {
    return this.httpClient.post(`${this.baseDomain}/user/createApiKey`, payload);
  }

  getApiKeys(userId: string) {
    return this.httpClient.get(`${this.baseDomain}/user/${userId}/apiKeys`);
  }

  revokeApiKey(payload: { username: string; id?: string; name?: string; key?: string }) {
    return this.httpClient.post(`${this.baseDomain}/user/revokeApiKey`, payload);
  }

  checkUser() {
    return this.httpClient.post(`${this.baseDomain}/user`, {});
  }

  updateUserRole(role) {
    const payload = { roles: [role] };
    return this.httpClient.patch(`${this.baseDomain}/user/current-role`, payload);
  }

  setUserAvatar(payload) {
    // let params = new HttpParams();
    // params = params.set('avatar', avatar);
    return this.httpClient.patch(`${this.baseDomain}/user/avatar`, payload).pipe(map((res: any) => res.data));
  }

  setCompanyLogo(payload) {
    // let params = new HttpParams();
    // params = params.set('logo', logo);
    // params = params.set('companyId', companyId);
    return this.httpClient.patch(`${this.baseDomain}/company/logo`, payload).pipe(map((res: any) => res.data));
  }

  uploadFile(payload) {
    return this.httpClient.patch(`${this.baseDomain}/user/file`, payload).pipe(map((res: any) => res));
  }

  removeFile(payload) {
    const { created, ...tosend } = payload;
    return this.httpClient.delete(`${this.baseDomain}/user/file`, { body: tosend });
  }

  downloadFile(file:any) {
    const { created, ...tosend } = file;
    return this.httpClient.post(`${this.baseDomain}/user/file/download`,tosend,{ responseType: 'blob' });
  }

  downloadCloudinaryFile(file) {
    const doc = !file.type.includes('image/') && !file.type.includes('/pdf') ? '/raw/upload/v1/' : '/';
    return this.httpClient.get(`${environment.cloudinaryContentUrl}${doc}${file.publicId}`, { responseType: 'blob' });
  }

  getCompaniesList(name, country = null) {
    let params = new HttpParams();
    params = params.set('name', name);
    if (country) {
      params = params.set('location', country);
    }
    return this.httpClient.get(`${this.baseDomain}/company`, { params }).pipe(map((res: any) => res.data));
  }

  createCompany(company) {
    return this.httpClient.post(`${this.baseDomain}/company`, company).pipe(map((res: any) => res.data));
  }

  updateCompany(company) {
    return this.httpClient.patch(`${this.baseDomain}/company`, company);
  }

  requestCompanyChange() {
    return this.httpClient.post(`${this.baseDomain}/request/company/change`, {});
  }

  reportCompanyChange(text) {
    return this.httpClient.post(`${this.baseDomain}/request/company/incorrect/info`, text);
  }

  setDefaultCv(fileUrl, type) {
    return this.httpClient.patch(`${this.baseDomain}/user/default/cv`, { fileUrl, type });
  }

  removeDefaultCv() {
    return this.httpClient.delete(`${this.baseDomain}/user/default/cv`);
  }

  //////////// Onboarding

  getOnboardingSteps() {
    return this.httpClient.get(`${this.baseDomain}/user/onboard/step`).pipe(map((res: any) => res));
  }

  setOnboardingSteps(stepItems, currentStep = 0) {
    const payload = { stepItems, currentStep, isComplited: false };
    return this.httpClient.patch(`${this.baseDomain}/user/onboard/step`, payload);
  }

  getJobTitles() {
    return this.httpClient.get(`${this.baseDomain}/job-title/all`).pipe(map((res: any) => res.data));
  }
  getJobTitlePresets(jobTitleId) {
    return this.httpClient.get(`${this.baseDomain}/job-title/${jobTitleId}/presets`).pipe(map((res: any) => res.data));
  }
  
  getJobTitleById(id) {
    return this.httpClient.get(`${this.baseDomain}/job-title/${id}`).pipe(map((res: any) => res.data));
  }

  saveJobTitle(jobTitle) {
    return this.httpClient.post(`${this.baseDomain}/job-title`, jobTitle).pipe(map((res: any) => res.data));
  }

  updateJobTitle(id, jobTitle) {
    jobTitle.jobTitle.Id = id;
    return this.httpClient.post(`${this.baseDomain}/job-title`,  { id, ...jobTitle} ).pipe(map((res: any) => res.data));
  }

  getUserSkillTree() {
    return this.httpClient.get(`${this.baseDomain}/user/job-title`).pipe(map((res: any) => res.data));
  }

  getUserHardSkills() {
    return this.httpClient.get(`${this.baseDomain}/user/hard-skill`).pipe(map((res: any) => res.data));
  }

  setUserHardSkills(branches) {
    return this.httpClient.patch(`${this.baseDomain}/user/hard-skill`, { branches });
  }

  getUserSoftSkills() {
    return this.httpClient.get(`${this.baseDomain}/user/soft-skill`).pipe(map((res: any) => res.data));
  }

  // User Management
  getAllUsers(isActive?: boolean | null) {
    let params = new HttpParams();
    if (typeof isActive === 'boolean') {
      params = params.set('isActive', String(isActive));
    }
    return this.httpClient.get(`${this.baseDomain}/user/all`, { params }).pipe(map((res: any) => res.data));
  }

  getAccountUsers(filterParams?: any) {
    let params = new HttpParams();
    if (filterParams) {
      if (filterParams.query) { params = params.set('query', filterParams.query); }
      params = params.set('take', filterParams.take ? filterParams.take : 50);
      params = params.set('skip', filterParams.skip ? filterParams.skip : 0);
    }
    return this.httpClient.get(`${this.baseDomain}/account/users`, { params }).pipe(map((res: any) => res.data || res));
  }

  enableUser(userId: string) {
    return this.httpClient.post(`${this.baseDomain}/user/enable/${userId}`, {});
  }

  disableUser(userId: string) {
    return this.httpClient.post(`${this.baseDomain}/user/disable/${userId}`, {});
  }

  inviteAccountUser(inviteUserPayload: {email: string, name: string, accountId: string}) {
    return this.httpClient.post(`${this.baseDomain}/account/invite`, inviteUserPayload).pipe(map((res: any) => res.data || res));
  }

  getAccountUsersSummary() {
    return this.httpClient.get(`${this.baseDomain}/account/users/summary`).pipe(map((res: any) => res?.data || res));
  }

  resendAccountInvitation(invitationId: string) {
    return this.httpClient
      .post(`${this.baseDomain}/account/invite/resend/${invitationId}`, {})
      .pipe(map((res: any) => res?.data || res));
  }

  validateInvitationToken(token: string): Observable<{ isValid: boolean; email?: string }> {
    return this.httpClient
      .get<{ data: { isValid: boolean; email?: string } } | any>(`${this.baseDomain}/account/invite/validate-token/${token}`)
      .pipe(map((res: any) => res?.data || res));
  }

  getAllCompanies() {
    return this.httpClient.get(`${this.baseDomain}/company/all`).pipe(map((res: any) => res.data));
  }

  setUserSoftSkills(skills) {
    return this.httpClient.patch(`${this.baseDomain}/user/soft-skill`, { skills });
  }

  getUserGeneralInfo(role: string) {
    return this.httpClient.get(`${this.baseDomain}/user/${role}/general-info`).pipe(map((res: any) => res));
  }

  updateUserGeneralInfo(role: string, info) {
    return this.httpClient.patch(`${this.baseDomain}/user/${role}/general-info`, info).pipe(map((res: any) => res));
  }

  resetTalentHardSkillsPreset() {
    return this.httpClient.patch(`${this.baseDomain}/user/hard-skill/reset`, {});
  }
  onboardComplete() {
    return this.httpClient.put(`${this.baseDomain}/user/onboard/complete`, {});
  }

  //////////// Create position

  createVacancy(jobPosition, jobPositions, jobDescription = '', selectedJobDescriptionFile: File | null = null) {
    const payload = this.createJobPositionFormData(
      null,
      jobPosition,
      jobPositions,
      jobDescription,
      selectedJobDescriptionFile
    );
    return this.httpClient.post(`${this.baseDomain}/vacancy`, payload).pipe(map((res: any) => res));
  }

  getCreatePositionSteps(vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/step/${vacancyId}`).pipe(map((res: any) => res));
  }

  setCreatePositionSteps(vacancyId, stepItems, currentStep = 0) {
    const payload = { vacancyId, stepItems, currentStep, isComplited: false };
    return this.httpClient.patch(`${this.baseDomain}/vacancy/step`, payload);
  }

  getVacancyById(vacancyId ) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/${vacancyId}`).pipe(map((res: any) => res.data));
  }

  setJobPosition(id, jobPosition, jobPositions, jobDescription = '', selectedJobDescriptionFile: File | null = null) {
    const payload = this.createJobPositionFormData(
      id,
      jobPosition,
      jobPositions,
      jobDescription,
      selectedJobDescriptionFile
    );
    return this.httpClient.patch(`${this.baseDomain}/vacancy/job-position`, payload);
  }

  enhanceDescriptionPrompt(descriptionPrompt: string) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/enhance-description-prompt`, {
      descriptionPrompt: descriptionPrompt
    }).pipe(map((res: any) => res?.data ?? res));
  }

  getVacancySkillTree(vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/job-title/${vacancyId}`).pipe(map((res: any) => res.data));
  }

  getVacancyHardSkills(vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/hard-skill/${vacancyId}`).pipe(map((res: any) => res.data));
  }

  setVacancyHardSkills(vacancyId, branches) {
    return this.httpClient.patch(`${this.baseDomain}/vacancy/hard-skill`, { vacancyId, branches });
  }

  getVacancySoftSkills(vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/soft-skill/${vacancyId}`).pipe(map((res: any) => res.data));
  }

  setVacancySoftSkills(vacancyId, skills) {
    return this.httpClient.patch(`${this.baseDomain}/vacancy/soft-skill`, { vacancyId, skills });
  }

  setVacancySoftSkillAssessments(vacancyId, assessmentIds) {
    return this.httpClient.patch(`${this.baseDomain}/vacancy/${vacancyId}/assessments`,  assessmentIds);
  }

  checkJobIdUnique(jobId: string, vacancyId?: string) {
    const params: any = { jobId };
    if (vacancyId) { params.vacancyId = vacancyId; }
    return this.httpClient.get<{ data: { isUnique: boolean } }>(`${this.baseDomain}/vacancy/jobid-exists`, { params })
      .pipe(map((res: any) => res?.data));
  }

  setVacancyInfo(id, payload) {
    return this.httpClient.patch(`${this.baseDomain}/vacancy/info`, { id, ...payload });
  }

  setVacancyStatus(id, status, jobBoards?: string[]) {
    // ‘draft’, ‘published’, ‘deleted’, ‘hired’
    const payload: any = { id, status };
    if (jobBoards) { payload.jobBoards = jobBoards; }
    return this.httpClient.patch(`${this.baseDomain}/vacancy/status`, payload);
  }

  resetHardSkillsPreset(vacancyId) {
    return this.httpClient.patch(`${this.baseDomain}/vacancy/hard-skill/reset`, { vacancyId });
  }

  acceptCv(cVUrl,vacancyId){
    const payload  = {cVUrl,vacancyId}
    return this.httpClient.post(`${this.baseDomain}/vacancy/accept-cv`, payload);
    
  }

    deleteCv(cVUrl,vacancyId){
    const payload  = {cVUrl,vacancyId}

    return this.httpClient.post(`${this.baseDomain}/vacancy/delete-accept-cv`, payload);
    
  }

  getVacancyFiles(vacancyId: string) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/${vacancyId}/files`);
  }

  getVacancyActivityLogs(vacancyId: string, filterParams?: { take?: number; skip?: number }) {
    let params = new HttpParams();
    params = params.set('take', String(filterParams?.take ?? 20));
    params = params.set('skip', String(filterParams?.skip ?? 0));

    return this.httpClient.get(`${this.baseDomain}/vacancy/${vacancyId}/activity`, { params });
  }


uploadCvs(files: File[], vacancyId: string, options?: { jobId?: string; sourcingType?: string; maxExtractCount?: number }) {
    const formData = new FormData();
    files.forEach(file => formData.append('file', file));

    let params = new HttpParams();
    if (options?.jobId) {
      params = params.set('jobId', options.jobId);
    }
    if (options?.sourcingType) {
      params = params.set('sourcingType', options.sourcingType);
    }
    if (options?.maxExtractCount != null && options.maxExtractCount > 0) {
      params = params.set('maxExtractCount', String(options.maxExtractCount));
    }

    return this.httpClient.post(
      `${this.baseDomain}/vacancy/${vacancyId}/upload`,
      formData,
      { params }
    );
  }

  getVacanciesByCompany(companyId: string, filterParams?: { query?: string; status?: string; take?: number; skip?: number }) {
    let params = new HttpParams();
    if (filterParams?.status) {
      params = params.set('status', filterParams.status);
    }
    if (filterParams?.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams?.take != null) {
      params = params.set('take', String(filterParams.take));
    }
    if (filterParams?.skip != null) {
      params = params.set('skip', String(filterParams.skip));
    }
    return this.httpClient.get(`${this.baseDomain}/vacancy/by-company/${companyId}`, { params });
  }

  getCompanyCredit(companyId: string): Observable<{ available: number }> {
    return this.httpClient.get<{ available: number }>(
      `${this.baseDomain}/company/${companyId}/candidate-credit`
    );
  }

  getCandidateEnrichmentBatches(query: CandidateEnrichmentBatchQuery = {}): Observable<CandidateEnrichmentBatchListDto> {
    let params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('pageSize', String(query.pageSize ?? 20));

    if (query.status) {
      params = params.set('status', query.status);
    }
    if (query.fromUtc) {
      params = params.set('fromUtc', query.fromUtc);
    }
    if (query.toUtc) {
      params = params.set('toUtc', query.toUtc);
    }
    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.sortDirection) {
      params = params.set('sortDirection', query.sortDirection);
    }

    return this.httpClient
      .get<any>(`${this.baseDomain}/candidate-enrichment/batches`, { params })
      .pipe(map((res: any) => res?.data || res));
  }

  getCandidateEnrichmentBatch(uploadId: string, query: CandidateEnrichmentRowsQuery = {}): Observable<CandidateEnrichmentBatchDetailDto> {
    let params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('pageSize', String(query.pageSize ?? 50));

    if (query.status) {
      params = params.set('status', query.status);
    }
    if (query.search) {
      params = params.set('search', query.search);
    }

    return this.httpClient
      .get<any>(`${this.baseDomain}/candidate-enrichment/batches/${encodeURIComponent(uploadId)}`, { params })
      .pipe(map((res: any) => res?.data || res));
  }


  //////////// Dashboard

  getStaticData(dataList = null ) {
    const all = [
      STAT_DATA.EducationDegree,
      STAT_DATA.EducationField,
      STAT_DATA.Currency,
      STAT_DATA.Domain,
      STAT_DATA.Language,
      STAT_DATA.LanguageLevel,
      STAT_DATA.CompanyType,
      STAT_DATA.CompanySize
    ];
    const params = { names: dataList ? dataList : all };
    return this.httpClient.get(`${this.baseDomain}/static-data`, { params }).pipe(map((res: any) => res.data));
  }

  //////////// Vacancies

  getVacancies(filterParams) {
    let params = new HttpParams();
    if (filterParams) {
      if (filterParams.status) { params = params.set('status', filterParams.status); }
      if (filterParams.query) { params = params.set('query', filterParams.query); }
      if (filterParams.ownerUserIds && filterParams.ownerUserIds.length) {
        filterParams.ownerUserIds.forEach(ownerUserId => {
          params = params.append('ownerUserIds', ownerUserId);
        });
      }
      if (filterParams.date && filterParams.date.startDate) { 
        params = params.set('start', filterParams.date.startDate.utc().format()); 
      }
      if (filterParams.date && filterParams.date.endDate) { 
        params = params.set('end', filterParams.date.endDate.utc().format()); 
      }
      params = params.set('take', filterParams && filterParams.take ? filterParams.take : 20);
      params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    }
    // this api returns vacancies by accountId so that we can show all vacancies to account Admin
    return this.httpClient.get(`${this.baseDomain}/vacancy/list`, { params }).pipe(map((res: any) => res.data));
  }

  getVacancyMatchByUserId(filterParams)
  {
    let params = new HttpParams();
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams && filterParams.locations && filterParams.locations.length) {
      params = params.set('locations', filterParams.locations.join(','));
    }
    if (filterParams && filterParams.dates) {
      params = params.set('startDate', filterParams.dates.start);
      params = params.set('endDate', filterParams.dates.end);
    }
   return this.httpClient.get(`${this.baseDomain}/vacancy/match-related-user`,{params}).pipe(map((res: any) => res.data));
  }
  

  contactUser(vacancyId, talentId = null) {
    let params = new HttpParams();
    params = params.set('VacancyId', vacancyId);
    if (talentId) {
      params = params.set('TalentId', talentId);
    }
    return this.httpClient.post(`${this.baseDomain}/vacancy/ask-question`, {}, { params }).pipe(map((res: any) => res.data));
  }

  confirmHire(vacancyId, talentId = null) {
    let params = new HttpParams();
    params = params.set('VacancyId', vacancyId);
    if (talentId) {
      params = params.set('TalentId', talentId);
    }
    return this.httpClient.post(`${this.baseDomain}/vacancy/confirm-hire`, {}, { params }).pipe(map((res: any) => res.data));
  }

  requestSourcing(vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/sourcing/request?jobId=${vacancyId}`);
  }

  vacancyApply(vacancyId) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/apply/${vacancyId}`, {}).pipe(map((res: any) => res.data));
  }

  cloneVacancy(vacancyId) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/clone/${vacancyId}`, {}).pipe(map((res: any) => res.data));
  }

  getInterestedTalents(vacancyId, filterParams = null) {
    let params = new HttpParams();
    if (filterParams && filterParams.query) { params = params.set('query', filterParams.query); }
    params = params.set('vacancyId', vacancyId);
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 20);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/vacancy/interested/users`, { params }).pipe(map((res: any) => res.data));
  }

  //////////// Messages

  getMessageList(filterParams) {
    let params = new HttpParams();
    if (filterParams && filterParams.type) {
      params = params.set('type', filterParams.type);
    }
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);

    return this.httpClient.get(`${this.baseDomain}/user/messages/last`, { params }).pipe(map((res: any) => res.data));
  }

  getApplies(filterParams) {
    let params = new HttpParams();
    if (filterParams && filterParams.sort) {
      params = params.set('sort', filterParams.sort);
    }
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams && filterParams.dates) {
      params = params.set('startDate', filterParams.dates.start);
      params = params.set('endDate', filterParams.dates.end);
    }
    if (filterParams && filterParams.matchLevel) {
      params = params.set('matchLevelMin', filterParams.matchLevel.min);
      params = params.set('matchLevelMax', filterParams.matchLevel.max);
    }
    if (filterParams && filterParams.locations && filterParams.locations.length) {
      params = params.set('locations', filterParams.locations);
    }
    if (filterParams && filterParams.vacancyIds && filterParams.vacancyIds.length) {
      params = params.set('vacancyIds', filterParams.vacancyIds.map(v => v.id));
    }
    params = params.set('type', filterParams && filterParams.type ? filterParams.type : 'all');
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/vacancy/applies`, { params }).pipe(map((res: any) => res.data));
  }

  deleteApplyById(applyId) {
    let params = new HttpParams();
    if (applyId) {
      params = params.set('vacancyEventId', applyId);
    }
    return this.httpClient.delete(`${this.baseDomain}/vacancy/apply/data`, { params });
  }

  markApplyReadById(applyId) {
    let params = new HttpParams();
    if (applyId) {
      params = params.set('vacancyEventId', applyId);
    }
    return this.httpClient.patch(`${this.baseDomain}/vacancy/apply/data/read`, {}, { params });
  }

  getNotifyList(filterParams) {
    let params = new HttpParams();
    params = params.set('type', filterParams && filterParams.type ? filterParams.type : 'all');
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/user/notify/last`, { params }).pipe(map((res: any) => res.data));
  }

  getMessages(threadId, filterParams) {
    let params = new HttpParams();
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/user/messages?MessageThreadId=${threadId}`, { params }).pipe(map((res: any) => res.data));
  }

  getMessageThreadData(threadId) {
    return this.httpClient.get(`${this.baseDomain}/user/message-thread/${threadId}`).pipe(map((res: any) => res.data));
  }

  sendMessage(messageThreadId, content, file = null) {
    const payload = { messageThreadId, content, file };
    return this.httpClient.post(`${this.baseDomain}/user/message`, payload);
  }

  markMessageReaded(messageId) {
    return this.httpClient.patch(`${this.baseDomain}/user/messages/read/${messageId}`, {});
  }

  markAllMessageReaded() {
    return this.httpClient.patch(`${this.baseDomain}/user/messages/read/all`, {});
  }

  declineMessageThread(threadId) {
    return this.httpClient.patch(`${this.baseDomain}/user/messages/decline/${threadId}`, {});
  }

  hideMessageThread(threadId) {
    let params = new HttpParams();
    params = params.set('messageThreadId', threadId);
    params = params.set('status', false);
    return this.httpClient.patch(`${this.baseDomain}/user/messages/status`, {}, { params });
  }

  removeNotify(notifyId) {
    let params = new HttpParams();
    if (notifyId) {
      params = params.set('id', notifyId);
    }
    return this.httpClient.delete(`${this.baseDomain}/notify`, { params });
  }

  //////////// Smart match 

  getMatchedVacancies(filterParams = null) {
    let params = new HttpParams();
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams && filterParams.locations && filterParams.locations.length) {
      params = params.set('locations', filterParams.locations.join(','));
    }
    if (filterParams && filterParams.dates) {
      params = params.set('startDate', filterParams.dates.start);
      params = params.set('endDate', filterParams.dates.end);
    }
    if (filterParams && filterParams.matchLevel) {
      params = params.set('matchLevelMin', filterParams.matchLevel.min);
      params = params.set('matchLevelMax', filterParams.matchLevel.max);
    }
    return this.httpClient.get(`${this.baseDomain}/smart-match/vacancies`, { params }).pipe(map((res: any) => res.data));
  }

  getDashboardTalents(filterParams = null) {
    let params = new HttpParams();
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/user/hm/dashboard/matches`, { params });
  }

  getDashboardRelatedMatches(filterParams = null) {
    let params = new HttpParams();
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/user/hm/dashboard/related-matches`, { params });
  }

  getRecruiterDashboardKpis() {
    return this.httpClient.get(`${this.baseDomain}/hm/dashboard/kpis`);
  }

  getRecruiterDashboardVacancies(limit = 5) {
    return this.httpClient.get(`${this.baseDomain}/hm/dashboard/vacancies`, {
      params: { limit: `${limit}` }
    });
  }

  getRecruiterDashboardCandidates(limit = 5) {
    return this.httpClient.get(`${this.baseDomain}/hm/dashboard/candidates`, {
      params: { limit: `${limit}` }
    });
  }

  showVacancyById(vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/details/${vacancyId}`).pipe(map((res: any) => res.data));
  }

  getMatchedTalents(vacancyId, filterParams = null) {
    let params = new HttpParams();
    params = params.set('vacancyId', vacancyId)
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams && filterParams.locations && filterParams.locations.length) {
      params = params.set('locations', filterParams.locations.join(','));
    }
    if (filterParams && filterParams.dates) {
      params = params.set('startDate', filterParams.dates.start);
      params = params.set('endDate', filterParams.dates.end);
    }
    if (filterParams && filterParams.matchLevel) {
      params = params.set('matchLevelMin', filterParams.matchLevel.min);
      params = params.set('matchLevelMax', filterParams.matchLevel.max);
    }
    if (filterParams && filterParams.isJobTitleValidated) {
      params = params.set('isJobTitleValidated', true);
    }
    if (filterParams && filterParams.candidateFilter) {
      params = params.set('candidateFilter', filterParams.candidateFilter);
    }
    //return this.httpClient.get(`${this.baseDomain}/smart-match/all/users`, { params }).pipe(map((res: any) => res.data));
    return this.httpClient.get(`${this.baseDomain}/vacancy/${vacancyId}/candidates`, { params }).pipe(map((res: any) => res.data));
  }

  getMatchedcandidates(vacancyId, filterParams = null) {
    let params = new HttpParams();
    params = params.set('vacancyId', vacancyId)
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 50);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams && filterParams.locations && filterParams.locations.length) {
      params = params.set('locations', filterParams.locations.join(','));
    }
    if (filterParams && filterParams.dates) {
      params = params.set('startDate', filterParams.dates.start);
      params = params.set('endDate', filterParams.dates.end);
    }
    if (filterParams && filterParams.matchLevel) {
      params = params.set('matchLevelMin', filterParams.matchLevel.min);
      params = params.set('matchLevelMax', filterParams.matchLevel.max);
    }
    if (filterParams && filterParams.isJobTitleValidated) {
      params = params.set('isJobTitleValidated', true);
    }
    return this.httpClient.get(`${this.baseDomain}/vacancy/${vacancyId}/candidates`, { params }).pipe(map((res: any) => res.data));
  }

  verifyCandidateUan(candidateId: string, vacancyId: string) {
    return this.httpClient.post(`${this.baseDomain}/verification/candidates/${candidateId}/verify`, { vacancyId });
  }

  findMoreCandidates(vacancyId) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/candidates`, {}).pipe(map((res: any) => res?.data));
  }

  syncNaukriApplicationsDeepmatch(vacancyId: string): Observable<{ vacancyId: string; processed: boolean; status: string }> {
    return this.httpClient
      .post<any>(`${this.baseDomain}/vacancy/${vacancyId}/naukri/applications/sync-deepmatch`, {})
      .pipe(map((res: any) => res?.data ?? res));
  }

  getOtherMatchedTalents(vacancyId, filterParams = null) {
    let params = new HttpParams();
    params = params.set('vacancyId', vacancyId)
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    return this.httpClient.get(`${this.baseDomain}/vacancy-users`,{params}).pipe(map((res: any) => res.data));
  }

  viewCandidateContact(vacancyId: string, talentId: string, detail: 'email' | 'phone' | 'whatsapp') {
    return this.httpClient.patch<ViewedContact>(
      `${this.baseDomain}/vacancy/${vacancyId}/candidates/${talentId}/viewed-detail`, { detail });
  }

  showTalentById(userId, vacancyId?: string) {
    let params = new HttpParams();
    if (vacancyId) {
      params = params.set('vacancyId', vacancyId);
  }
    return this.httpClient.get(`${this.baseDomain}/user/talent/details/${userId}`, { params });
  }

  getTalentEmailAndVacancyId(talentId:any) {
    return this.httpClient.get(`${this.baseDomain}/user/talent/related-user/${talentId}`).pipe(map((res: any) => res));
  }

  showMatchById(matchId) {
    return this.httpClient.get(`${this.baseDomain}/smart-match/${matchId}`).pipe(map((res: any) => res.data));
  }

  downloadMatchedProfile(matchId, format: 'pdf' | 'docx') {
    const params = new HttpParams().set('format', format);
    return this.httpClient.get(`${this.baseDomain}/talent-cv/${matchId}/download`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  downloadOriginalResume(candidateId: string) {
    return this.httpClient.get(`${this.baseDomain}/user/talent/${candidateId}/resume`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  showCandidateMatchById(matchId, vacancyId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/${vacancyId}/${matchId}`).pipe(map((res: any) => res.data));
  }

  recalculateMatch(vacancyId = null) {
    let params = new HttpParams();
    if (vacancyId) {
      params = params.set('vacancyId', vacancyId);
    }
    return this.httpClient.get(`${this.baseDomain}/smart-match/recalculate`, { params });
  }

  searchHardSkills = (query) => {
    let params = new HttpParams();
    params = params.set('query', query);
    return this.httpClient.get(`${this.baseDomain}/hard-skill`, { params }).pipe(map((res: any) => res.data));
  }

  emulate401() {
    return this.httpClient.get(`${this.baseDomain}/unauthorized`);
  }

  connectVacancy(vacancyId, messageThreadId) {
    let params = new HttpParams();
    params = params.set('vacancyId', vacancyId);
    params = params.set('messageThreadId', messageThreadId);
    return this.httpClient.patch(`${this.baseDomain}/user/message-thread/vacancy`, {}, { params });
  }

  getVacancyMatchList(talentId) {
    return this.httpClient.get(`${this.baseDomain}/vacancy/match-list/${talentId}`).pipe(map((res: any) => res.data));
  }

  retrieveCertificates(code) {
    return this.httpClient.post(`${this.baseDomain}/messenger/provider/retrieve/certificate`, { code });
  }
  
 getAssessmentLink(email: string,searchDto: {
  jobTitle: string;
  branches: string[];
  skill: string[];
  softSkill: string;
}): Observable<any> {
  let params = new HttpParams()
    .set('jobTitle', searchDto.jobTitle)
    .set('softSkill', searchDto.softSkill);

  searchDto.branches.forEach(b => {
    params = params.append('branches', b);
  });

  searchDto.skill.forEach(s => {
    params = params.append('skill', s);
  });
  return this.httpClient.get(`${this.baseDomain}/assessment/invite/${email}`,{params});
}

CheckAssessment(searchDto: {
  jobTitle: string;
  branches: string[];
  skill: string[];
  jobTitles: string[];
}): Observable<any> {
  let params = new HttpParams()
    .set('jobTitle', searchDto.jobTitle);

  searchDto.branches.forEach(b => {
    params = params.append('branches', b);
  });

  searchDto.skill.forEach(s => {
    params = params.append('skill', s);
  });

  searchDto.jobTitles.forEach(jt => {
    params = params.append('jobTitles', jt);
  });

  return this.httpClient.get(`${this.baseDomain}/assessment/skill`, { params });
}

  getRequestedValidations(): Observable<any> {
  return this.httpClient.get(`${this.baseDomain}/user/job-title/validate/requested`);
}

  getAdminJobBoardJobs(board: string, filter: { page?: number; pageSize?: number; status?: string } = {}): Observable<any> {
    let params = new HttpParams()
      .set('page', String(filter.page ?? 1))
      .set('pageSize', String(filter.pageSize ?? 25));

    if (filter.status) {
      params = params.set('status', filter.status);
    }

    return this.httpClient.get(`${this.baseDomain}/admin/job-boards/${encodeURIComponent(board)}/jobs`, { params });
  }

  getAdminJobBoardJob(board: string, vacancyId: string): Observable<any> {
    return this.httpClient.get(
      `${this.baseDomain}/admin/job-boards/${encodeURIComponent(board)}/jobs/${encodeURIComponent(vacancyId)}`
    );
  }

  retryAdminJobBoardJob(board: string, vacancyId: string): Observable<any> {
    return this.httpClient.post(
      `${this.baseDomain}/admin/job-boards/${encodeURIComponent(board)}/jobs/${encodeURIComponent(vacancyId)}/retry`,
      ''
    );
  }

  refreshPendingAdminJobBoardJobs(board: string): Observable<any> {
    return this.httpClient.post(
      `${this.baseDomain}/admin/job-boards/${encodeURIComponent(board)}/jobs/refresh-pending`,
      ''
    );
  }



  //////////// Shared links

  getSharedVacancy(id) {
    return this.httpClient.get(`${this.baseDomain}/share/vacancy/${id}`);
  }

  getSharedTalent(id) {
    return this.httpClient.get(`${this.baseDomain}/share/user/${id}`);
  }

  //////////// Guest mode

  getPublicVacancies(filterParams) {
    let params = new HttpParams();
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    if (filterParams && filterParams.dates) {
      params = params.set('startDate', filterParams.dates.start);
      params = params.set('endDate', filterParams.dates.end);
    }
    if (filterParams && filterParams.locations && filterParams.locations.length) {
      params = params.set('locations', filterParams.locations);
    }
    if (filterParams && filterParams.jobTitles && filterParams.jobTitles.length) {
      params = params.set('jobTitles', filterParams.jobTitles.map(val => val.name));
    }
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 10);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    return this.httpClient.get(`${this.baseDomain}/guest/vacancies`, { params });
  }

  getVersion() {
    return this.httpClient.get(`${this.baseDomain}/soft/version`).pipe(map((res: any) => res.data));
  }

  telegramConnect(token) {
    return this.httpClient.post(`${this.baseDomain}/messenger/provider/connect`, { token });
  }

  getSkillsValidation() {
    return this.httpClient.get(`${this.baseDomain}/user/job-title/validate`);
  }

  getSkillsValidationSummary() {
    return this.httpClient.get(`${this.baseDomain}/user/job-title/validate/summary`);
  }

  activateSkillsValidation() {
    return this.httpClient.post(`${this.baseDomain}/user/job-title/validate`, {});
  }

  setSkillsValidationViewed(jobTitle = null, skill = null, branch = null, softSkill = null) {
    return this.httpClient.patch(`${this.baseDomain}/user/job-title/view`, { jobTitle, skill, branch, softSkill });
  }

  getSalaryRange(values) {
    let params = new HttpParams();
    params = params.set('country', values.country);
    params = params.set('position', values.position);
    params = params.set('seniority', values.seniority ? values.seniority : 'middle');
    params = params.set('currency', values.currency ? values.currency : 'USD');
    return this.httpClient.get(`${this.baseDomain}/salary/range`, { params });
  }

  //////////// Settings 

  getUserNotificationSettings() {
    return this.httpClient.get(`${this.baseDomain}/user/notification/settings`);
  }

  updateUserNotificationSettings(type, channel, enable) {
    let params = new HttpParams();
    params = params.set('type', type);
    params = params.set('channel', channel);
    params = params.set('enable', enable);
    return this.httpClient.patch(`${this.baseDomain}/user/notification/settings`, {}, { params });
  }

  toggleUserNotification(enable) {
    let params = new HttpParams();
    params = params.set('enable', enable);
    return this.httpClient.patch(`${this.baseDomain}/user/notification/enable`, {}, { params });
  }

  getBranches() {
    let params = new HttpParams();
    params = params.set('query', '');
    return this.httpClient.get(`${this.baseDomain}/hard-skill`, { params }).pipe(map((res: any) => res.data));
  }
  
  getSkills() {
    let params = new HttpParams();
    params = params.set('query', '');
    return this.httpClient.get(`${this.baseDomain}/hard-skill`, { params }).pipe(map((res: any) => 
      res.data.flatMap((branch: any) => branch.skills.map((skill: any) => ({...skill, branch: branch.name })))));
  }
  
  saveSkill(skill: any) {
    return this.httpClient.post(`${this.baseDomain}/hard-skill/skill`, skill).pipe(map((res: any) => res.data));
  }
  
  saveBranch(branch: any) {
    return this.httpClient.post(`${this.baseDomain}/hard-skill`, branch).pipe(map((res: any) => res.data));
  }

   getSkillAssessmentbyId(id : any)
  {
    return this.httpClient.get(`${this.baseDomain}/assessment/${id}`).pipe(map((res: any) => res));
  }

  postSkillAssessment(dto : any)
  {
    return this.httpClient.post(`${this.baseDomain}/assessment`, dto).pipe(map((res: any) => res.data));
  }

  getAllConnection()
  {
    return this.httpClient.get(`${this.baseDomain}/connection/all`).pipe(map((res:any)=>res.data))
  }
   addConnection(dto:any)
  {
    return this.httpClient.post(`${this.baseDomain}/connection`, dto).pipe(map((res:any)=>res.data))
  }
  connectionGetById(id:any) {
    return this.httpClient.get(`${this.baseDomain}/connection/${id}`).pipe(map((res:any)=>res.data))
  }
  ConnectConnection(id:any) {
    return this.httpClient.patch(`${this.baseDomain}/connect/${id}`,{}).pipe(map((res:any)=>res.data))
  }

  DisconnectConnection(id:any) {
    return this.httpClient.patch(`${this.baseDomain}/disconnect/${id}`,{}).pipe(map((res:any)=>res.data))
  }
  deleteConnection(id:any) {
    return this.httpClient.delete(`${this.baseDomain}/connection/${id}`).pipe(map((res:any)=>res.data))
  }
 updateConnection(dto: any) {
  return this.httpClient.patch(`${this.baseDomain}/connectionupdate`, dto).pipe(map((res: any) => res.data)
  );
}
fetchDataFromConnection(id:any) {
    return this.httpClient.patch(`${this.baseDomain}/connection/fetch/${id}`,{}).pipe(map((res:any)=>res.data))
  }

  updateVacancyApprovedForAutomation(vacancyId: string): Observable<boolean> {
    return this.httpClient.post(`${this.baseDomain}/vacancy/startAutomation/${vacancyId}`, {}).pipe(map((res:any)=>res.data));
  }

  getAutoEmailEngagementInit(
    vacancyId: string,
    payload: { sequenceIds?: string[] }
  ): Observable<AutoEmailEngagementInitResponse> {
    let params = new HttpParams();
    if (payload?.sequenceIds?.length) {
      payload.sequenceIds.forEach(id => {
        params = params.append('sequenceIds', id);
      });
    }
    return this.httpClient
      .get<any>(`${this.baseDomain}/vacancy/${vacancyId}/auto-email-engagement/recipients`, { params })
      .pipe(map(res => res?.data ?? res));
  }

  getAutoEmailTemplate(key: string, language: string): Observable<AutoEmailTemplateResponse> {
    return this.httpClient
      .get<any>(`${this.baseDomain}/templates/search`, { params: { key, language } })
      .pipe(map(res => res?.data ?? res));
  }

  activateAutoEmailEngagementCampaign(
    vacancyId: string,
    request: AutoEmailEngagementActivateRequest
  ): Observable<AutoEmailEngagementActivateResponse> {
    return this.httpClient
      .post<any>(`${this.baseDomain}/vacancy/${vacancyId}/auto-email-engagement/send`, request)
      .pipe(map(res => res?.data ?? res));
  }

  getAutoEmailEngagementSequences(query: AutoEmailEngagementSequencesQuery): Observable<AutoEmailEngagementSequencesResponse> {
    let params = new HttpParams()
      .set('take', String(query.take ?? SEQUENCE_CHUNK_SIZE))
      .set('skip', String(query.skip ?? 0));

    if (query.filter && query.filter) {
      params = params.set('filter', query.filter);
    }

    if (query.query) {
      params = params.set('query', query.query);
    }

    return this.httpClient
      .get<any>(`${this.baseDomain}/vacancy/${query.vacancyId}/auto-email-engagement/sequences`, { params })
      .pipe(map(res => res?.data ?? res));
  }

  getCandidateEmailEngagement(vacancyId: string, candidateId: string): Observable<CandidateEmailEngagementHistory> {
    return this.httpClient
      .get(`${this.baseDomain}/vacancy/${vacancyId}/candidates/${candidateId}/email-engagement`)
      .pipe(map((res: any) => res?.data ?? res));
  }

  updateAutoEmailSequenceStage(
    vacancyId: string,
    sequenceId: string,
    action: 'mark_applied' | 'close'
  ): Observable<boolean> {
    return this.httpClient
      .patch(`${this.baseDomain}/vacancy/${vacancyId}/auto-email-engagement/sequences/${sequenceId}/stage`, { action })
      .pipe(map((res: any) => !!(res?.data ?? res)));
  }

  boostVacancy(vacancyId: string): Observable<number> {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/boost`, {}).pipe(map((res: any) => res?.data));
  }

  changeAccuracy(vacancyId: string, forceHighAccuracy: boolean): Observable<any> {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/changeAccuracy`, forceHighAccuracy ).pipe(map((res: any) => res?.data));
  }

  getConfigDescriptors(filterParams): Observable<PagedResponse<'items', ConfigurationItemDescriptor>> {
    let params = new HttpParams();

    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 100);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);

    return this.httpClient.get(`${this.baseDomain}/config/descriptors`, { params }).pipe(map((res: any) => res.data));
  }

  getConfigValues(filterParams, id: string): Observable<PagedResponse<'values', ConfigurationItemValue>> {
    let params = new HttpParams();
    if (filterParams && filterParams.query) {
      params = params.set('query', filterParams.query);
    }
    params = params.set('take', filterParams && filterParams.take ? filterParams.take : 100);
    params = params.set('skip', filterParams && filterParams.skip ? filterParams.skip : 0);
    params = params.set('Key', id);
    return this.httpClient.get(`${this.baseDomain}/config/values`, { params }).pipe(map((res: any) => res.data));
    }

  deleteConfigValue(id: string) {
    return this.httpClient.delete(`${this.baseDomain}/config/values/${id}`);
  }

  AddUpdateConfigValue(configValueData: ConfigurationItemValue) {
      return this.httpClient.post(`${this.baseDomain}/config/values`, configValueData).pipe(map((res: any) => res.data));
  }

  //////////// Translations / Resources

  getTranslationResources(languageCode: string) {
    return this.httpClient.get(`${this.baseDomain}/config/resources/${languageCode}`).pipe(map((res: any) => res.data || res));
  }

  updateTranslationResources(languageCode: string, payload: Record<string, any>) {
    return this.httpClient.post(`${this.baseDomain}/config/resources/${languageCode}`, payload).pipe(map((res: any) => res.data || res));
  }

  getAvailableLanguages() {
    return this.httpClient.get(`${this.baseDomain}/config/resources/available`).pipe(map((res: any) => res.data || res));
  }

  saveUserLanguage(languageCode: string) {
    return this.httpClient.post(`${this.baseDomain}/config/language`, { languageCode }).pipe(map((res: any) => res.data || res));
  }

  createChargebeeHostedPage(planId: string, customerEmail: string): Observable<any> {
    return this.httpClient.post(`${this.baseDomain}/chargebee/create-hosted-page`, {
      planId,
      customerEmail
    }).pipe(map((res: any) => res));
  }

  getPricingPlans(): Observable<any> {
    return this.httpClient.get(`${this.baseDomain}/pricing-plans`).pipe(map((res: any) => res.data || res));
  }

  getAccountPlan(accountId: string): Observable<any> {
    return this.httpClient.get(`${this.baseDomain}/account/${accountId}/plan`).pipe(map((res: any) => res.data || res));
  }

    getInvitationCompanyDetails(): Observable<any> {
      return this.httpClient.get(`${this.baseDomain}/invitation/company-details`).pipe(map((res: any) => res.data || res));
  }

  //////////// Add-ons

  purchaseAddon(request: IAddonPayload) {
    return this.httpClient
      .post(`${this.baseDomain}/config/addons/Buy`, request)
      .pipe(map((res: any) => res.data || res));
  }

  requestAddon(request: IAddonPayload) {
    return this.httpClient
      .post(`${this.baseDomain}/config/addons/request`, request)
      .pipe(map((res: any) => res.data || res));
  }

  getAddonRequests(filter?: { status?: string; query?: string; take?: number; skip?: number }) {
    let params = new HttpParams();
    if (filter?.status) {
      params = params.set('status', filter.status);
    }
    if (filter?.query) {
      params = params.set('query', filter.query);
    }
    params = params.set('take', (filter?.take ?? 50).toString());
    params = params.set('skip', (filter?.skip ?? 0).toString());

    return this.httpClient
      .get(`${this.baseDomain}/config/addons/requests`, { params })
      .pipe(map((res: any) => res.data || res));
  }
  
  approveAddonRequest(id: string) {
    return this.httpClient
      .put(`${this.baseDomain}/config/addons/requests/${id}/approve`, {})
      .pipe(map((res: any) => res.data || res));
  }

  rejectAddonRequest(id: string) {
    return this.httpClient
      .put(`${this.baseDomain}/config/addons/requests/${id}/reject`, {})
      .pipe(map((res: any) => res.data || res));
  }

  //////////// Candidate marking (like/archive)

  likeCandidate(vacancyId: string, candidateId: string) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/like/${candidateId}`, {});
  }

  unlikeCandidate(vacancyId: string, candidateId: string) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/unlike/${candidateId}`, {});
  }

  archiveCandidate(vacancyId: string, candidateId: string) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/archive/${candidateId}`, {});
  }

  unarchiveCandidate(vacancyId: string, candidateId: string) {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/unarchive/${candidateId}`, {});
  }
  getRoles(){
    return this.httpClient.get<string[]>(`${this.baseDomain}/user/roles`).pipe(map((res: any) => res?.data || res));
  }

  setUserRoles(userId: string, roles: string[]) {
    return this.httpClient.post(`${this.baseDomain}/user/${userId}/roles`, roles).pipe(map((res: any) => res?.data || res));
  }
 //////////// Webhooks
  getWebhooks(filter: { status: string; query?: string | null; take?: number; skip?: number }) {
    let params = new HttpParams();
    params = params.set('status', filter.status);
    params = params.set('take', (filter.take ?? 50).toString());
    params = params.set('skip', (filter.skip ?? 0).toString());
    if (filter.query) {
      params = params.set('query', filter.query);
    }

    return this.httpClient
      .get<{ data: { counters: any; webhooks: WebhookSubscription[] } }>(`${this.baseDomain}/webhooks/all`, { params })
      .pipe(map((res: any) => res));
  }

  createWebhook(payload: Partial<WebhookSubscription>) {
    return this.httpClient
      .post<{ data: WebhookSubscription }>(`${this.baseDomain}/webhooks`, payload)
      .pipe(map((res: any) => res?.data || res));
  }

  updateWebhook(id: string, payload: Partial<WebhookSubscription>) {
    return this.httpClient
      .put<{ data: WebhookSubscription }>(`${this.baseDomain}/webhooks/${id}`, payload)
      .pipe(map((res: any) => res?.data || res));
  }

  setWebhookStatus(id: string, isActive: boolean) {
    return this.httpClient
      .patch(`${this.baseDomain}/webhooks/${id}/status`, { isActive })
      .pipe(map((res: any) => res?.data || res));
  }

  duplicateWebhook(id: string) {
    return this.httpClient
      .post<{ data: WebhookSubscription }>(`${this.baseDomain}/webhooks/${id}/duplicate`, {})
      .pipe(map((res: any) => res?.data || res));
  }

  testWebhook(request: WebhookTestRequest) {
    return this.httpClient
      .post(`${this.baseDomain}/webhooks/${request.webhookId}/test`, {
        contextSample: request.contextSample
      })
      .pipe(map((res: any) => res?.data || res));
  }

  deleteWebhook(id: string) {
    return this.httpClient
      .delete(`${this.baseDomain}/webhooks/${id}`)
      .pipe(map((res: any) => res?.data || res));
  }
  
  getWebhookEvents() {
    return this.httpClient
      .get<{ data: string[] }>(`${this.baseDomain}/webhooks/events`)
      .pipe(map((res: any) => res?.data || res));
  }

  getCities(): Observable<string[]> {
    return this.httpClient.get<any>(`${this.baseDomain}/geo/countries/india/cities`).pipe(
      map((res: any) => res?.data || res),
    );
  }

  private createJobPositionFormData(
    id,
    jobPosition,
    jobPositions,
    roleDescription = '',
    selectedJobDescriptionFile: File | null = null
  ) {
    const formData = new FormData();

    if (id) {
      formData.append('id', id);
    }

    formData.append('jobPosition', jobPosition || '');
    formData.append('jobPositions', JSON.stringify(jobPositions || []));
    formData.append('jobDescription', roleDescription || '');

    if (selectedJobDescriptionFile) {
      formData.append('jobDescriptionFile', selectedJobDescriptionFile, selectedJobDescriptionFile.name);
    }

    return formData;
  }
  trackRecruiterActivity(vacancyId: string, events: RecruiterActivityRequestModel[]) {
    return this.httpClient.post(`${this.baseDomain}/analytics/recruiter/candidate-profile/events`, {
      vacancyId,
      events
    });
  }

  //////////// DeepMatch Priority Weights

  saveAccountDeepMatchWeights(accountId: string, weights: DeepMatchPriorityWeights): Observable<any> {
    return this.resolveDeepMatchDescriptor().pipe(
      switchMap((descriptor) =>
        this.AddUpdateConfigValue({
          itemId: descriptor.id,
          scope: 'Account',
          accountId,
          value: JSON.stringify({
            HardSkills: weights.hardSkills,
            SoftSkills: weights.softSkills,
            RequiredExperience: weights.requiredExperience,
            Salary: weights.salary,
            CompanyDomain: weights.companyDomain,
            WorkPlace: weights.workPlace,
            EmploymentType: weights.employmentType,
            VerifiedUser: weights.verifiedUser,
            Relocation: weights.relocation,
            Education: weights.education,
            Language: weights.language,
            CompanySize: weights.companySize,
            UserSkillValidation: weights.userSkillValidation
          })
        })
      )
    );
  }

  resetAccountDeepMatchWeights(accountId: string): Observable<any> {
    return this.resolveDeepMatchDescriptor().pipe(
      switchMap((descriptor) =>
        this.findAccountDeepMatchValue(descriptor.id!, accountId).pipe(
          switchMap((row) =>
            row?.id ? this.deleteConfigValue(row.id) : of(null)
          )
        )
      )
    );
  }

  getVacancyDeepMatchSettings(vacancyId: string): Observable<DeepMatchEffectiveSettings> {
    return this.httpClient.get<DeepMatchEffectiveSettings>(
      `${this.baseDomain}/vacancy/${vacancyId}/deepmatch/settings`
    );
  }

  saveVacancyDeepMatchWeights(vacancyId: string, weights: DeepMatchPriorityWeights): Observable<any> {
    return this.httpClient.put(`${this.baseDomain}/vacancy/${vacancyId}/deepmatch/settings`, { weights });
  }

  resetVacancyDeepMatchWeights(vacancyId: string): Observable<any> {
    return this.httpClient.delete(`${this.baseDomain}/vacancy/${vacancyId}/deepmatch/settings`);
  }

  rescoreVacancyDeepMatch(vacancyId: string): Observable<any> {
    return this.httpClient.post(`${this.baseDomain}/vacancy/${vacancyId}/deepmatch/rescore`, {});
  }

  private resolveDeepMatchDescriptor(): Observable<ConfigurationItemDescriptor> {
    return this.getConfigDescriptors({
      query: DEEPMATCH_CONFIG_NAME,
      take: 100,
      skip: 0
    }).pipe(
      map((res) => {
        const items = res?.items || [];
        const descriptor = items.find(
          (item) =>
            item.group === DEEPMATCH_CONFIG_GROUP &&
            item.name === DEEPMATCH_CONFIG_NAME
        );
        if (!descriptor?.id) {
          throw new Error('DeepMatchWeights configuration descriptor not found');
        }
        return descriptor;
      })
    );
  }

  private findAccountDeepMatchValue(
    descriptorId: string,
    accountId: string
  ): Observable<ConfigurationItemValue | null> {
    return this.getConfigValues({ query: accountId, take: 100, skip: 0 }, descriptorId).pipe(
      map((res) =>
        (res?.values || []).find(
          (v) => v.scope === 'Account' && v.accountId === accountId && !!v.value
        ) || null
      )
    );
  }

}

